import { Trans } from '@lingui/react';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { DisplayIdentity, Identity } from '@startuptools/business-ui/identity/Identity';
import { Table } from '@startuptools/ui/Table';
import { GqlMeetingParticipant, GqlMeeting } from '../../../../../graphql/base-types.graphql';
import { useMpSetFunctionsMutation, useMpSetPresentMutation } from './FunctionSelect.graphql';
import { BoardMeetingDocument } from '../../../../board-meetings/BoardMeeting.graphql';
import { useMemo } from 'react';
import { Alert } from '@startuptools/ui/Alert';
import { Stack } from '@startuptools/ui/Stack';
import { CircularProgress } from '@startuptools/ui/joy/CircularProgress';
import { genericErrorToast } from '@startuptools/ui/toast/use-toast';

export const AiBmFunctionSelect = ({
  meeting,
}: {
  meeting: Pick<GqlMeeting, 'id' | 'companyId'> & {
    meetingParticipants: (Pick<GqlMeetingParticipant, 'id' | 'present' | 'chairman' | 'secretary' | 'adjuster'> & {
      identity: DisplayIdentity;
      proxy?: DisplayIdentity | null;
    })[];
  };
}) => {
  const [doSetPresent, { loading: loadingSetPresent }] = useMpSetPresentMutation({
    refetchQueries: [BoardMeetingDocument],
  });
  const [doSetFunctions, { loading: loadingSetFunctions }] = useMpSetFunctionsMutation({
    refetchQueries: [BoardMeetingDocument],
  });

  const errorChairman = useMemo(
    () => !meeting.meetingParticipants.some(mp => mp.chairman),
    [meeting.meetingParticipants],
  );
  const errorAdjusters = useMemo(
    () => meeting.meetingParticipants.length > 1 && !meeting.meetingParticipants.some(mp => mp.adjuster),
    [meeting.meetingParticipants],
  );

  return (
    <Stack gap={3}>
      <Table sx={{ tableLayout: 'auto' }}>
        <thead>
          <tr>
            <th style={{ width: 20 }}>
              <Stack gap={1} height="100%" justifyContent="flex-end">
                <CircularProgress
                  size="sm"
                  sx={{ visibility: loadingSetPresent || loadingSetFunctions ? 'visible' : 'hidden' }}
                />
              </Stack>
            </th>

            <th>
              <Trans id="Present" />
            </th>
            <th>
              <Trans id="Participant" />
            </th>
            <th>
              <Trans id="Chairman" />
            </th>
            <th>
              <Trans id="Secretary" />
            </th>
            <th>
              <Trans id="Adjuster" />
            </th>
          </tr>
        </thead>
        <tbody>
          {meeting.meetingParticipants.map(mp => (
            <tr key={mp.id}>
              <td></td>
              <td style={{ width: 90 }}>
                <form>
                  <Checkbox
                    checked={mp.present}
                    onChange={async ev => {
                      try {
                        const input = { id: mp.id, present: ev.target.checked };
                        await doSetPresent({
                          variables: { companyId: meeting.companyId, input },
                          optimisticResponse: {
                            mpSetPresent: input,
                          },
                        });
                      } catch {
                        genericErrorToast();
                      }
                    }}
                  />
                </form>
              </td>
              <td>
                <Identity identity={mp.identity} />
              </td>
              <td style={{ width: 100 }}>
                <Checkbox
                  checked={mp.chairman}
                  onChange={async ev => {
                    try {
                      await doSetFunctions({
                        variables: {
                          companyId: meeting.companyId,
                          input: { ids: [mp.id], chairman: ev.target.checked },
                        },
                        optimisticResponse: {
                          mpSetFunctions: meeting.meetingParticipants.map(m => ({
                            ...m,
                            chairman: ev.target.checked ? m.id === mp.id : !ev.target.checked,
                          })),
                        },
                      });
                    } catch {
                      genericErrorToast();
                    }
                  }}
                />
              </td>
              <td style={{ width: 100 }}>
                <Checkbox
                  checked={mp.secretary}
                  onChange={ev =>
                    doSetFunctions({
                      variables: {
                        companyId: meeting.companyId,
                        input: { ids: [mp.id], secretary: ev.target.checked },
                      },
                      optimisticResponse: {
                        mpSetFunctions: meeting.meetingParticipants.map(m => ({
                          ...m,
                          secretary: m.id === mp.id ? ev.target.checked : m.secretary,
                          adjuster: m.id === mp.id ? !ev.target.checked : m.adjuster,
                        })),
                      },
                    })
                  }
                />
              </td>
              <td style={{ width: 100 }}>
                <Checkbox
                  checked={mp.adjuster}
                  onChange={ev =>
                    doSetFunctions({
                      variables: {
                        companyId: meeting.companyId,
                        input: { ids: [mp.id], adjuster: ev.target.checked },
                      },
                      optimisticResponse: {
                        mpSetFunctions: meeting.meetingParticipants.map(m => ({
                          ...m,
                          adjuster: m.id === mp.id ? ev.target.checked : m.adjuster,
                          secretary: m.id === mp.id ? !ev.target.checked : m.secretary,
                        })),
                      },
                    })
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {errorChairman && (
        <Alert color="danger">
          <Trans id="You have to pick a chairman" />
        </Alert>
      )}
      {errorAdjusters && (
        <Alert color="danger">
          <Trans id="You have to pick at least one verifier" />
        </Alert>
      )}
    </Stack>
  );
};
